import { t } from '@lingui/macro';
import { ErrorMessage } from 'components/ErrorMessage';
import { SuspendWithRelativelyCenteredSpinner } from 'components/suspense/SuspendWithRelativelyCenteredSpinner';
import partition from 'lodash/partition';
import React from 'react';
import { ErrorBoundary } from 'utils/errors';
import { graphql, useSafeRelay } from 'utils/graphClient';
import { notEmpty } from 'utils/NotEmptyFilter';
import { MemberSelector, MemberSelectorProps } from '.';
import { SpaceMemberSelector_members_Query } from './__generated__/SpaceMemberSelector_members_Query.graphql';

const membersQuery = graphql`
  query SpaceMemberSelector_members_Query($spaceId: ID!) {
    node(id: $spaceId) {
      ... on Space {
        __typename
        id
        name
        memberships(first: 500) {
          edges {
            node {
              id
              roles
              member {
                id
                name
                avatar: image(width: 750) {
                  url
                }
                ...UserHandle_member
                status {
                  value
                }
              }
            }
          }
        }
      }
    }
  }
`;

interface SpaceMemberSelector
  extends Omit<
    MemberSelectorProps,
    'selectedMembers' | 'usersInitiallyAvailableForAddition'
  > {
  spaceId: string;
  selectedMemberIds: string[];
}

export const SpaceMemberSelector = (props: SpaceMemberSelector) => {
  return (
    <ErrorBoundary
      fallback={
        <ErrorMessage
          description={t`Members cannot be loaded, please try again later`}
        />
      }
    >
      <SuspendWithRelativelyCenteredSpinner>
        <UnsuspendedSpaceMemberSelector {...props} />
      </SuspendWithRelativelyCenteredSpinner>
    </ErrorBoundary>
  );
};

const UnsuspendedSpaceMemberSelector = ({
  spaceId,
  selectedMemberIds,
  ...props
}: SpaceMemberSelector) => {
  const { useLazyLoadQuery } = useSafeRelay();
  const { node } = useLazyLoadQuery<SpaceMemberSelector_members_Query>(
    membersQuery,
    { spaceId },
    { fetchPolicy: 'store-and-network' },
  );

  if (node?.__typename !== 'Space') {
    throw new Error('Invalid space for UnsuspendedSpaceMemberSelector');
  }

  const members = node.memberships.edges
    .map((edge) => edge.node?.member)
    .filter(notEmpty);

  let [membersInitiallyAvailableForAddition, selectedMembers] = partition(
    members,
    ({ id }) =>
      !selectedMemberIds.find((selectedMemberId) => selectedMemberId === id),
  );

  const spaceName = node.name;
  return (
    <MemberSelector
      usersInitiallyAvailableForAddition={membersInitiallyAvailableForAddition}
      selectedMembers={selectedMembers}
      selectAllCTA={t`Select all members of ${spaceName}`}
      allowSelectAll
      {...props}
    />
  );
};
